"use client";

import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  number: "01",
  name: "Brand Identity",
  location: "Digital Strategy",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_brand_identity.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_brand_identity.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_brand_identity.png-wvc-sizes",
  alt: "Brand Identity — modern digital strategy for global brands",
  colSpan: "md:col-span-7 md:col-start-1",
  aspect: "aspect-[16/10]",
  offset: "md:mt-0"
}, {
  number: "02",
  name: "Content Creation",
  location: "Social Media",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_content_creation.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_content_creation.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_content_creation.webp-wvc-sizes",
  alt: "Content Creation — high-impact social media assets",
  colSpan: "md:col-span-4 md:col-start-9",
  aspect: "aspect-[3/4]",
  offset: "md:mt-20"
}, {
  number: "03",
  name: "Growth Hacking",
  location: "Performance Marketing",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_growth_hacking.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_growth_hacking.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_growth_hacking.webp-wvc-sizes",
  alt: "Growth Hacking — data-driven performance marketing",
  colSpan: "md:col-span-5 md:col-start-2",
  aspect: "aspect-square",
  offset: "md:mt-52"
}, {
  number: "04",
  name: "Influencer Campaigns",
  location: "Community Engagement",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_influencer_campaigns.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_influencer_campaigns.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_influencer_campaigns.png-wvc-sizes",
  alt: "Influencer Campaigns — authentic community engagement",
  colSpan: "md:col-span-5 md:col-start-7",
  aspect: "aspect-[4/3]",
  offset: "md:mt-32"
}, {
  number: "05",
  name: "Video Production",
  location: "Short-form Content",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_video_production.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_video_production.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_video_production.webp-wvc-sizes",
  alt: "Video Production — viral short-form video content",
  colSpan: "md:col-span-4 md:col-start-1",
  aspect: "aspect-[3/4]",
  offset: "md:mt-16"
}, {
  number: "06",
  name: "Community Management",
  location: "Audience Retention",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_community_management.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_community_management.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/social_community_management.webp-wvc-sizes",
  alt: "Community Management — building loyal digital audiences",
  colSpan: "md:col-span-7 md:col-start-6",
  aspect: "aspect-[21/9]",
  offset: "md:mt-40"
}];
export default function EditorialServiceGallery() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return;
}
type Project = {
  number: string;
  name: string;
  location: string;
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
  colSpan: string;
  aspect: string;
  offset: string;
};
function ProjectCard({
  project,
  index,
  ...rest
}: {
  project: Project;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={ref} className={`${project.colSpan} ${project.offset} transition-all duration-[1200ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
    transitionDelay: `${index % 3 * 80}ms`
  }}>
      <Link to="/HomePage" className="group block relative">
        <div className={`relative overflow-hidden rounded-lg ${project.aspect} bg-muted`}>
          <img src={project.src} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 h-[108%] w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]" />

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

          <div className="absolute bottom-6 right-6 translate-x-4 translate-y-4 opacity-0 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-700 ease-out">
            <div className="flex size-12 items-center justify-center rounded-lg bg-primary backdrop-blur-sm">
              <ArrowUpRight className="size-5 text-white" />
            </div>
          </div>

          <div className="absolute bottom-6 left-6 right-20 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-700 ease-out">
            <span className="block font-default font-bold text-[10px] tracking-[0.3em] uppercase text-white/80">
              Project № {project.number}
            </span>
            <h3 className="mt-2 font-default font-bold text-2xl md:text-3xl leading-tight text-white">
              {project.name}
            </h3>
            <p className="mt-1 text-xs font-default text-white/70">
              {project.location}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-baseline justify-between">
          <div>
            <span className="font-default font-bold text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              № {project.number}
            </span>
            <h4 className="mt-1.5 font-default font-bold text-xl text-foreground">
              {project.name}
            </h4>
          </div>
          <span className="font-default text-xs text-muted-foreground">
            {project.location}
          </span>
        </div>
      </Link>
    </div>;
}